import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { scrollTo } from '@/core/utils/dom/scrollTo'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useKeyDownArrows } from '@/hooks/useKeyDownArrows'
import { useMedia } from '@/hooks/useMedia'
import { useScrollActivatedItem } from '@/hooks/useScrollActivatedItem'

import type { CarouselItemSize, CarouselProps } from '../types'

import { CarouselActions } from './CarouselActions'
import MediaItem from './MediaItem'
import {
  CarouselInnerStyles,
  CarouselItemStyles,
  CarouselStyles,
  CarouselWrapperStyles,
} from './styles'

const Carousel = ({ className, itemGap = 1, slides }: CarouselProps) => {
  const isMobile = useIsMobile('600px')

  const size: CarouselItemSize = useMemo(() => {
    if (isMobile) {
      return {
        width: 180,
        height: 108,
        activeWidth: 200,
        activeHeight: 120,
      }
    }
    return {
      width: 482,
      height: 265,
      activeWidth: 586,
      activeHeight: 322,
    }
  }, [isMobile])

  const carouselRef = useRef<HTMLOListElement>(null)
  const countSlides = slides.length
  const [{ active, smoothness }, setCarousel] = useState({
    active: 1,
    count: countSlides,
    smoothness: false,
  })
  const totalSlides = slides.length

  const handleActiveIndex = useCallback(
    (index: number) => {
      if (index === active) {
        return
      }

      setCarousel((state) => ({
        ...state,
        active: index,
        smoothness: false,
      }))
    },
    [active],
  )

  const curryHandleActive = useCallback((action: 'next' | 'prev') => {
    setCarousel((state) => {
      const incrementDecrement = (index: number) => index + (action === 'next' ? 1 : -1)
      const { active, count } = state
      const newActive = incrementDecrement(active)
      const isLastIsActive = newActive >= count
      const isInvalidIndex = newActive < 0 || newActive > count
      if (isLastIsActive || isInvalidIndex) {
        return state
      }

      return {
        ...state,
        active: newActive,
        smoothness: true,
      }
    })
  }, [])

  const handleNext = useCallback(() => curryHandleActive('next'), [curryHandleActive])
  const handlePrev = useCallback(() => curryHandleActive('prev'), [curryHandleActive])

  const handleOnFocusSlide = useCallback(() => {
    setCarousel((oldState) => ({ ...oldState, smoothness: false }))
  }, [])

  useEffect(() => {
    if (carouselRef.current && active >= 0) {
      const index = active

      const element = carouselRef.current?.children.item(index) as HTMLElement
      scrollTo(element, 'center')
    }
  }, [active, countSlides, isMobile])

  useEffect(() => {
    if (isMobile) {
      setCarousel((oldState) => ({
        ...oldState,
        smoothness: true,
      }))
    }
  }, [isMobile])

  useScrollActivatedItem({
    onActive: handleActiveIndex,
    active,
    ref: carouselRef,
    debounceDelay: isMobile ? 400 : 50,
    viewPercentage: isMobile ? 30 : 40,
    enabled: useMedia(`(max-width: 2560px)`),
  })

  const { ref: keyDownRef } = useKeyDownArrows({ onRight: handleNext, onLeft: handlePrev })

  return (
    <CarouselStyles ref={keyDownRef} className={className}>
      <CarouselWrapperStyles>
        <CarouselInnerStyles ref={carouselRef} role="list" smoothness={smoothness}>
          {slides.map((item, index) => {
            const isActive = active === index

            const contentAlign = () => {
              if (active > index) return 'end'
              if (active < index) return 'start'
              return 'center'
            }

            return (
              <CarouselItemStyles
                itemGap={itemGap}
                onFocus={handleOnFocusSlide}
                key={`carousel_item_${index}`}
                contentAlign={contentAlign()}
                size={{
                  width: isActive ? size.activeWidth : size.width,
                  height: size.activeHeight,
                }}
              >
                <MediaItem
                  isActive={isActive}
                  active={active}
                  totalSlides={totalSlides}
                  size={size}
                  item={item}
                />
              </CarouselItemStyles>
            )
          })}
        </CarouselInnerStyles>
      </CarouselWrapperStyles>

      {totalSlides > 1 && (
        <CarouselActions
          onNext={handleNext}
          onPrev={handlePrev}
          onActive={handleActiveIndex}
          currentSlide={active}
          totalSlides={totalSlides}
        />
      )}
    </CarouselStyles>
  )
}

export default Carousel
